import type {
  AbsChord,
  Label,
  Pc,
  Quality,
  Ring,
  ScaleId,
  Seventh,
} from '@astrasonora/annotation-schema'
import { cellId } from '@astrasonora/annotation-schema'
import { degreeIndex, diatonicQuality, inScale, mod12, scaleOf } from './scales.ts'
import { numeralOf, spell } from './sky.ts'

export const TRIAD: Record<Quality, readonly number[]> = {
  maj: [0, 4, 7],
  min: [0, 3, 7],
  dim: [0, 3, 6],
  aug: [0, 4, 8],
  sus2: [0, 2, 7],
  sus4: [0, 5, 7],
}

export const SEV: Record<Exclude<Seventh, 'none'>, number> = { min7: 10, maj7: 11, dim7: 9 }

/** Pitch classes of a chord relative to the tonic. */
function chordPcs(abs: AbsChord, tonic: Pc): number[] {
  const rel = mod12(abs.root - tonic)
  const pcs = TRIAD[abs.quality].map((t) => mod12(rel + t))
  if (abs.seventh !== 'none') pcs.push(mod12(rel + SEV[abs.seventh]))
  return pcs
}

function isDiatonic(abs: AbsChord, tonic: Pc, S: readonly number[]): boolean {
  return chordPcs(abs, tonic).every((pc) => inScale(pc, S))
}

/** Extension suffix: the seventh on the label, replacing a diminished symbol where it must. */
function extension(quality: Quality, seventh: Seventh): string {
  if (seventh === 'none') return ''
  if (quality === 'maj' && seventh === 'min7') return '7'
  if (quality === 'maj' && seventh === 'maj7') return 'maj7'
  if (quality === 'min' && seventh === 'min7') return '7'
  if (quality === 'dim' && seventh === 'min7') return 'ø7'
  if (quality === 'dim' && seventh === 'dim7') return '°7'
  return seventh === 'maj7' ? '(maj7)' : seventh === 'dim7' ? '(°7)' : '7'
}

const isSus = (q: Quality): boolean => q === 'sus2' || q === 'sus4'

/**
 * The ring a chord is drawn on (spec §3): its triad quality; an augmented chord the major ring of
 * its root, a suspended chord the ring its root's scale chord would have — both never asked.
 */
function ringOf(abs: AbsChord, rel: number, S: readonly number[]): Ring {
  if (abs.quality === 'maj' || abs.quality === 'min' || abs.quality === 'dim') return abs.quality
  if (abs.quality === 'aug') return 'maj'
  const d = degreeIndex(rel, S)
  if (d === null) return 'maj'
  const q = diatonicQuality(d, S)
  return q === 'maj' || q === 'min' || q === 'dim' ? q : 'maj'
}

/**
 * The Roman numeral label of a chord relative to the declared tonic and scale (spec §3): the cell
 * from root and quality, the numeral spelled from the tonic's major scale, and the text with its
 * context — a chord from outside the scale resolving a fifth down to a chord of the scale is named
 * by what it does (V/V, V7/IV). `next` is the following chord in the song, quiz flag irrelevant.
 */
export function labelChord(abs: AbsChord, next: AbsChord | null, tonic: Pc, scale: ScaleId): Label {
  const S = scaleOf(scale)
  const rel = mod12(abs.root - tonic)
  const ring = ringOf(abs, rel, S)
  const cell = cellId(rel, ring)
  const { degree, acc } = spell(rel, ring)
  const own = numeralOf(degree, acc, ring) + (abs.quality === 'aug' ? '+' : '')
  const hasSeventh = abs.seventh !== 'none'
  const ext = extension(abs.quality, abs.seventh)

  let text: string | null = null
  if (
    !isDiatonic(abs, tonic, S) &&
    abs.quality === 'maj' &&
    (abs.seventh === 'none' || abs.seventh === 'min7') &&
    next !== null &&
    (next.quality === 'maj' || next.quality === 'min') &&
    mod12(next.root - tonic) === mod12(rel + 5)
  ) {
    const nextRel = mod12(next.root - tonic)
    const triadDiatonic = TRIAD[next.quality].every((t) => inScale(nextRel + t, S))
    if (triadDiatonic && nextRel !== 0) {
      const target = spell(nextRel, next.quality)
      text = `V${hasSeventh ? '7' : ''}/${numeralOf(target.degree, target.acc, next.quality)}`
    }
  }
  if (text === null) {
    if (abs.quality === 'dim' && (ext.startsWith('ø') || ext === '°7'))
      text = own.replace('°', '') + ext
    else text = own + (isSus(abs.quality) ? abs.quality : '') + ext
  }
  return { rel, degree, cell, own, text, hasSeventh }
}
